import { z } from "zod";
import { apiRequest } from "../../../../shared/api/client";
import type { Rfq, UnifiedEvaluation, ContractCompliance } from "../../domain/rfq-types";

const evaluationSchema = z.object({
  id: z.string().min(1), version: z.number(), status: z.string(), topOfferId: z.string().nullable(), comparisonCurrency: z.string(),
  offers: z.array(z.object({
    id: z.string(), supplier: z.string(), source: z.enum(["MANUAL", "PORTAL"]), sourceLabel: z.string(),
    totalScore: z.number().nullable(), comparableTotal: z.number().nullable(), deliveryDays: z.number().nullable(), paymentDays: z.number().nullable(),
    exclusionReasons: z.array(z.string()), rank: z.number().nullable(),
  }).passthrough()),
}).passthrough();
const complianceSchema = z.object({
  offerVersionId: z.string().optional(), result: z.enum(["FULLY_COMPLIANT", "COMPLIANT_WITH_WARNINGS", "OUTSIDE_CONTRACT"]), evaluatedAt: z.string(),
  checks: z.array(z.object({ field: z.string(), label: z.string(), status: z.enum(["PASS", "WARNING", "FAIL"]), actual: z.string(), contract: z.string(), difference: z.string(), reason: z.string() }).passthrough()),
}).passthrough();
const orderSchema = z.discriminatedUnion("created", [
  z.object({ created: z.literal(true), orderNo: z.string().min(1), reason: z.string(), mode: z.enum(["SHADOW", "ACTIVE"]).optional() }).passthrough(),
  z.object({ created: z.literal(false), reason: z.string().min(1), mode: z.enum(["SHADOW", "ACTIVE"]).optional() }).passthrough(),
]);
const statusSchema = z.object({ status: z.string().min(1) });
const invalidResponse = () => Object.assign(new Error("Teklif işleminin sunucu yanıtı doğrulanamadı."), { code: "INVALID_API_RESPONSE" });
const post = (path: string, payload: unknown) => apiRequest<unknown>(path, {
  method: "POST", headers: { "Idempotency-Key": crypto.randomUUID() }, body: JSON.stringify(payload),
});
export async function extendRfqDeadline(rfq: Rfq, deadlineAt: string, reason: string) {
  const result = statusSchema.safeParse(await post(`/api/rfqs/${rfq.id}/deadline-extension`, { expectedVersion: rfq.current_version ?? rfq.currentVersion, deadlineAt, reason }));
  if (!result.success) throw invalidResponse();
  return result.data.status;
}
export async function unsealRfq(rfq: Rfq, reason: string) {
  await post(`/api/rfqs/${rfq.id}/unseal`, { expectedVersion: rfq.current_version ?? rfq.currentVersion, reason });
}
export async function evaluateRfqOffers(rfqId: string): Promise<UnifiedEvaluation> {
  const result = evaluationSchema.safeParse(await post(`/api/rfqs/${rfqId}/evaluations`, {}));
  if (!result.success) throw invalidResponse();
  return result.data;
}
export async function evaluateRfqContract(rfqId: string, offerVersionId: string): Promise<ContractCompliance> {
  const result = complianceSchema.safeParse(await post(`/api/contract-compliance/${rfqId}`, { offerVersionId }));
  if (!result.success || result.data.offerVersionId !== undefined && result.data.offerVersionId !== offerVersionId) throw invalidResponse();
  return { ...result.data, offerVersionId };
}
export async function assessRfqContractOrder(rfqId: string, offerVersionId: string) {
  const result = orderSchema.safeParse(await post(`/api/contract-orders/${rfqId}`, { offerVersionId }));
  if (!result.success) throw invalidResponse();
  return result.data;
}
export async function publishRfqAnnouncement(rfqId: string, body: string) {
  await post(`/api/rfqs/${rfqId}/announcements`, { body });
}
export async function answerRfqQuestion(rfqId: string, questionId: string, draft: { body: string; visibility: "ALL_INVITEES" | "PRIVATE" }) {
  await post(`/api/rfqs/${rfqId}/questions/${questionId}/answer`, draft);
}
